import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { LocalDateTime } from "@js-joda/core";
import { motion } from "framer-motion";
import React from "react";
import { formatPriceUSD } from "../../../format";
import { AddressComponents } from "../../../messages";
import { STRINGS } from "../../../strings";
import { AppCaregiver } from "../../../system/AppState/AppCaregiverState";
import { Visit } from "../../../system/AppState/AppVisitsState";
import { globalize, translate } from "../../../translate";
import { getColor } from "../../../utils/generateColor";
import { ArrowCircleRightOutlineIcon, CurrencyCircleDollarIcon } from "../../../utils/icones";
import {
  getStatusColor,
  getStatusText,
  getVisitDuration,
  getVisitStatus,
  getVisitTypeText,
  VisitStatus,
} from "../../AvailableVisitsAndTasksList";
import { AppButton } from "../../Generic/AppButton";
import TravelTimeEstimator from "../../TravelTimesEstimator";

type Props = {
  caregiver: Pick<AppCaregiver, "addressGeoLocation">;
  visit: Visit;
  onClick: (visit: Visit) => void;
};

function VisitMapCard(props: Props, ref: React.Ref<HTMLDivElement>) {
  const { patientInitials, startTimeLocal, endTimeLocal, location, price, type } =
    props.visit.publicVisitBroadcastDetails;
  const [propertiesRef, setPropertiesRef] = React.useState<HTMLDivElement | null>(null);

  return (
    <Root ref={ref}>
      <Header>
        <Avatar>{patientInitials}</Avatar>
        <HeaderContent>
          <ScheduleDate>{formatDate(startTimeLocal, endTimeLocal)}</ScheduleDate>
          <Address>{formatAddress(location)}</Address>
        </HeaderContent>
        {price !== null && (
          <VisitPrice>
            <VisitPriceValue>{formatPriceUSD(price)}/h</VisitPriceValue>
            <VisitPriceIcon as={CurrencyCircleDollarIcon} />
          </VisitPrice>
        )}
      </Header>
      <VisitSecondHeader>
        <VisitTypeAndDuration>
          <VisitType>{getVisitTypeText(type)}</VisitType> /{" "}
          <VisitDuration>{getVisitDuration(props.visit)}</VisitDuration>
        </VisitTypeAndDuration>
        <VisitMapCardStatus visit={props.visit} />
      </VisitSecondHeader>
      <VisitProperties>
        <VisitPropertiesContainer
          isScrollable={
            propertiesRef ? propertiesRef.scrollWidth === propertiesRef.clientWidth : false
          }
          ref={setPropertiesRef}
          transition={{
            duration: 3,
            ease: "easeInOut",
            repeat: Infinity,
            repeatType: "mirror",
            repeatDelay: 2,
          }}
          animate={{
            x: [
              "0px",
              propertiesRef ? `-${propertiesRef.scrollWidth - propertiesRef.clientWidth}px` : "0px",
            ],
          }}
        >
          {props.visit.publicVisitBroadcastDetails.visitInfo?.map((property) => (
            <VisitProperty key={property}>{property}</VisitProperty>
          ))}
          <VisitProperty>
            {props.visit.publicVisitBroadcastDetails.patientGender === "F"
              ? translate(STRINGS.FEMALE)
              : translate(STRINGS.MALE)}
          </VisitProperty>
        </VisitPropertiesContainer>
      </VisitProperties>

      <Hr />

      <TravelTimeEstimator
        source={props.caregiver.addressGeoLocation}
        destination={location.location}
      />

      <Hr />

      <ButtonWrapper>
        <VisitInformationButton size="large" onClick={() => props.onClick(props.visit)}>
          <span>{translate(STRINGS.VISIT_INFORMATION)}</span>
          <ArrowCircleRightOutlineIcon fontSize={24} />
        </VisitInformationButton>
      </ButtonWrapper>
    </Root>
  );
}

export default React.forwardRef(VisitMapCard);

function formatDate(startTimeLocal: LocalDateTime, endTimeLocal: LocalDateTime) {
  const format = (date: LocalDateTime) =>
    globalize().formatLocalDateTime(
      date,
      startTimeLocal.year() === endTimeLocal.year() ? { skeleton: "MMMd" } : { date: "short" }
    );

  const startTime = format(startTimeLocal);
  const endTime = format(endTimeLocal);

  return startTime === endTime ? startTime : `${startTime} - ${endTime}`;
}

function formatAddress(address: AddressComponents): string {
  return [address.sublocalityLevel1, address.neighborhood, address.locality]
    .filter(Boolean)
    .join(", ");
}

const Root = styled(motion.div)`
  background: white;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.08);
  border-radius: 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 12px 12px 0;
  margin-bottom: 12px;
`;

const Avatar = styled.div<{ children: string }>`
  --size: 56px;
  background-color: ${({ children }) => getColor(children)};
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  width: var(--size);
  height: var(--size);
  border-radius: 20px;
  font-size: 16px;
  font-weight: 700;
  border: 4px solid white;
  margin-top: -24px;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ScheduleDate = styled.span`
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
`;

const Address = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--tw-slate-500);
`;

const VisitPrice = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-inline-start: auto;
  align-self: flex-start;
  padding: 8px;
`;

const VisitPriceValue = styled.span``;

const VisitPriceIcon = styled.svg`
  font-size: 24px;
  color: var(--tw-slate-400);
`;

const VisitSecondHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 12px 16px;
`;

const VisitTypeAndDuration = styled.div`
  font-size: 14px;
  line-height: 20px;
`;

const VisitType = styled.span`
  color: var(--tw-slate-500);
  font-weight: 600;
`;

const VisitDuration = styled.strong`
  color: var(--tw-slate-900);
  font-weight: 700;
  text-transform: lowercase;
`;

const VisitProperty = styled.span`
  font-weight: 700;
  font-size: 11px;
  line-height: 20px;
  text-transform: uppercase;
  text-align: center;
  white-space: nowrap;
  color: #475569;
`;

const VisitProperties = styled.div`
  background-color: var(--tw-slate-50);
  border-radius: 8px;
  padding: 6px 8px;
  margin: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VisitPropertiesContainer = styled(motion.div)<{ isScrollable: boolean }>`
  display: flex;
  width: 100%;

  ${(props) => {
    if (props.isScrollable) {
      return css`
        align-items: center;
        justify-content: center;
      `;
    }

    return css``;
  }}

  ${VisitProperty} + ${VisitProperty} {
    &::before {
      content: "";
      display: inline-block;
      width: 1px;
      height: 5px;
      border-left: 1px solid var(--tw-slate-300);
      margin-inline-start: 12px;
      padding-inline-start: 12px;
    }
  }
`;

const Hr = styled.div`
  height: 1px;
  margin: 8px 0;
  background-color: var(--tw-slate-100);
`;

function VisitMapCardStatus(props: { visit: Visit }) {
  const status = getVisitStatus(props.visit);
  const statusAsText = getStatusText(status).split(" ")[0];

  return <Root2 status={status}>{statusAsText}</Root2>;
}

const Root2 = styled.div<{ status: VisitStatus }>`
  --bg-color: black;
  --text-color: white;
  background-color: var(--bg-color);
  color: var(--text-color);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  padding: 4px 8px;
  text-transform: capitalize;

  display: flex;

  ${(props) => {
    switch (getStatusColor(props.status)) {
      case "blue":
        return css`
          --bg-color: var(--tw-blue-50);
          --text-color: var(--tw-blue-600);
        `;
      case "yellow":
        return css`
          --bg-color: var(--tw-amber-50);
          --text-color: var(--tw-amber-600);
        `;
      case "green":
        return css`
          --bg-color: var(--tw-emerald-50);
          --text-color: var(--tw-emerald-600);
        `;
      case "slate":
        return css`
          --bg-color: var(--tw-slate-100);
          --text-color: var(--tw-slate-600);
        `;
    }
  }}
`;

const ButtonWrapper = styled.div`
  margin-top: 12px;
  padding: 0 12px 12px 12px;
`;

const VisitInformationButton = styled(AppButton)`
  font-size: 16px;
  font-weight: 600;
  width: 100%;
`;

export function duration(seconds: number): string {
  const minute = 60;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (seconds < minute) {
    return globalize().formatUnit(seconds, "second", { form: "short" });
  }

  if (seconds < hour) {
    const minutes = Math.floor(seconds / minute);
    seconds %= minute;
    return globalize().formatUnit(minutes, "minute", { form: "short" });
  }

  if (seconds < day) {
    const hours = Math.floor(seconds / hour);
    const minutes = Math.floor((seconds % hour) / minute);
    seconds %= minute;

    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  const days = Math.floor(seconds / day);
  return `${days}d`;
}
